import React, { useEffect, useRef, useState } from "react";
import schedulerService from "../../../services/scheduler";
import Icon from "./icons";
import WeekPicker from "./WeekPicker";
import { useQuickBuildDock } from "./quickBuildDock";
import { fmtRange, MON_SHORT, isoDate } from "./scheduleUtils";
import css from "./Scheduler.module.css";
import "./SchedulerCreate.css";

const parseISODate = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const prettyDate = (s) => {
  const d = parseISODate(s);
  return `${MON_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};
const hhmmToMin = (s) => {
  const [h, m] = String(s).split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

/* Minimal custom dropdown (reuses the global .cdd styles from SchedulerCreate.css). */
function SelectDD({ placeholder, value, options, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [open]);
  const sel = options.find((o) => o.value === value);
  return (
    <div className={`cdd ${open ? "open" : ""} ${disabled ? "disabled" : ""}`} ref={ref}>
      <button
        type="button"
        className="cdd-btn"
        disabled={disabled}
        onClick={(e) => {
          if (disabled) return;
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {sel ? <span>{sel.label}</span> : <span className="cdd-ph">{placeholder}</span>}
        <Icon name="chevronDown" size={16} />
      </button>
      <div className="cdd-menu">
        {options.length === 0 ? (
          <div className="cdd-opt" style={{ color: "var(--app-text-muted)", cursor: "default" }}>
            <div className="co-name">No options</div>
          </div>
        ) : (
          options.map((o) => (
            <div
              key={o.value || "__empty"}
              className={`cdd-opt ${o.value === value ? "sel" : ""}`}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              <div className="co-name">{o.label}</div>
              <span className="co-chk"><Icon name="check" size={15} /></span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/**
 * QuickBuildFab — a floating "+" control for rapidly building a schedule.
 *
 * Flow: pick a staff member → choose Client or Facility → pick a Shift template,
 * then drag the resulting chip onto a day in the (week) calendar to place it.
 * The chip stays armed after a drop so multiple days can be filled quickly.
 *
 * The armed selection is lifted to the parent via onArmChange(payload|null);
 * the parent owns the calendar drop target and the createShift call.
 */
// Default shift window when no template is chosen (shift is optional): 9 AM–5 PM.
const DEFAULT_START = 9 * 60;
const DEFAULT_END = 17 * 60;

export default function QuickBuildFab({ staff = [], admin = null, clients = [], facilities = [], placedCount = 0, onArmChange, onPlaceNow }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null); // null (speed-dial) | 'shift' | 'appointment'
  const [locType, setLocType] = useState("client"); // 'client' | 'facility'
  const [locId, setLocId] = useState("");
  const [apptTitle, setApptTitle] = useState("");
  const [apptDate, setApptDate] = useState(""); // YYYY-MM-DD
  const [apptTime, setApptTime] = useState(""); // HH:MM start (typed)
  const [apptEndTime, setApptEndTime] = useState(""); // HH:MM end (typed)
  const [apptAllDay, setApptAllDay] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [dateMonth, setDateMonth] = useState(() => new Date());
  const [pendingAppts, setPendingAppts] = useState([]); // appointments waiting to be dragged
  const [templateKey, setTemplateKey] = useState("");
  const [customStart, setCustomStart] = useState("09:00"); // HH:MM (Custom shift)
  const [customEnd, setCustomEnd] = useState("17:00");
  const [templates, setTemplates] = useState([]);
  const [dragId, setDragId] = useState(null);
  const dateRef = useRef(null);

  // Dock/float state (mirrors SkAi): the FAB only appears once released from the
  // toggle pill next to "Add"; while docked it stays tucked away. When floating
  // it can be dragged anywhere and its position persists across refreshes.
  const [dock, setDock] = useQuickBuildDock();
  const floating = dock.popped;
  const dragRef = useRef({ moved: false });
  const startFabDrag = (e) => {
    if (!floating || e.button !== 0) return;
    const wrap = e.currentTarget.closest("[data-qb-wrap]");
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const offX = e.clientX - rect.left;
    const offY = e.clientY - rect.top;
    const startX = e.clientX;
    const startY = e.clientY;
    dragRef.current.moved = false;
    const onMove = (ev) => {
      if (!dragRef.current.moved && Math.hypot(ev.clientX - startX, ev.clientY - startY) < 4) return;
      dragRef.current.moved = true;
      const x = Math.max(4, Math.min(window.innerWidth - rect.width - 4, ev.clientX - offX));
      const y = Math.max(4, Math.min(window.innerHeight - rect.height - 4, ev.clientY - offY));
      setDock({ x, y });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  useEffect(() => {
    if (!dateOpen) return undefined;
    const h = (e) => {
      if (dateRef.current && !dateRef.current.contains(e.target)) setDateOpen(false);
    };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [dateOpen]);

  const isAppt = mode === "appointment";
  const closeAll = () => {
    setOpen(false);
    setMode(null);
    // reset to an empty state so the next open starts fresh
    setLocId("");
    setApptTitle("");
    setApptDate("");
    setApptTime("");
    setApptEndTime("");
    setApptAllDay(false);
    setTemplateKey("");
    setCustomStart("09:00");
    setCustomEnd("17:00");
    setDateOpen(false);
    setPendingAppts([]);
  };

  useEffect(() => {
    let active = true;
    schedulerService.getTemplates().then((t) => {
      if (active) setTemplates(t || []);
    });
    return () => {
      active = false;
    };
  }, []);

  // Docking the FAB away also closes any open speed-dial/panel.
  useEffect(() => {
    if (!floating) closeAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floating]);

  // A chip is only dimmed while it is actively being dragged. Clear the dragging
  // state after each placement so a reused staff member never looks disabled
  // (onDragEnd can be skipped when the source re-renders on drop).
  useEffect(() => {
    setDragId(null);
  }, [placedCount]);

  // Reset the location selection when toggling type.
  const pickType = (t) => {
    setLocType(t);
    setLocId("");
  };

  const tmpl = templates.find((t) => (t._id || t.id) === templateKey);
  const locSelected = !!locId;

  // The panel is "active" once a client/facility is picked, which enables the
  // calendar day columns as drop targets. The actual shift details travel with
  // each staff chip's drag payload, so the parent only needs the active flag.
  const dropActive = open && (mode === "shift" ? locSelected : pendingAppts.length > 0);
  useEffect(() => {
    if (onArmChange) onArmChange(dropActive ? true : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropActive]);

  // De-duplicate templates by name+time so the picker isn't cluttered.
  const seenTpl = new Set();
  const templateOptions = templates.reduce((acc, t) => {
    const k = `${t.name}|${t.startMin}|${t.endMin}`;
    if (seenTpl.has(k)) return acc;
    seenTpl.add(k);
    acc.push({ value: t._id || t.id, label: `${t.name} · ${fmtRange(t.startMin, t.endMin)}` });
    return acc;
  }, []);
  // Let users set their own hours without a saved template.
  templateOptions.push({ value: "custom", label: "Custom time…" });

  const locOptions = locType === "client" ? clients : facilities;
  const selClient = locType === "client" ? clients.find((c) => c.value === locId) : null;
  const selFacility = locType === "facility" ? facilities.find((f) => f.value === locId) : null;

  // Staff associated with the chosen client/facility:
  //  - facility: staff whose facilityId includes it
  //  - client:   the client's explicitly-assigned staff, falling back to the
  //              staff of the client's facility(ies)
  const assocStaff = (() => {
    if (!locSelected) return [];
    if (locType === "facility") {
      return staff.filter((s) => (s.facilityId || []).includes(locId));
    }
    const assigned = selClient?.staffIds || [];
    let list = staff.filter((s) => assigned.includes(s.value));
    if (!list.length) {
      const facs = selClient?.facilityId || [];
      list = staff.filter((s) => (s.facilityId || []).some((f) => facs.includes(f)));
    }
    return list;
  })();

  // The business admin is schedulable too — appended at the bottom (deduped in
  // case the admin also appears in the associated staff).
  const dragStaff =
    admin && !assocStaff.some((s) => s.value === admin.value)
      ? [...assocStaff, admin]
      : assocStaff;

  // Shift mode times: a saved template, custom hours, or the 9–5 default.
  const isCustom = templateKey === "custom";
  const shiftStart = isCustom ? hhmmToMin(customStart) : tmpl ? tmpl.startMin : DEFAULT_START;
  const shiftEnd = isCustom ? hhmmToMin(customEnd) : tmpl ? tmpl.endMin : DEFAULT_END;
  // Appointment mode time is typed. End defaults to +1h from start (or 9–5).
  const apptStart = apptTime ? hhmmToMin(apptTime) : DEFAULT_START;
  const apptEnd = apptEndTime
    ? hhmmToMin(apptEndTime)
    : apptTime
    ? hhmmToMin(apptTime) + 60
    : DEFAULT_END;
  // A client's facilityId entries may be populated objects — reduce to a plain id
  // so the shift payload sends a valid ObjectId string (not "[object Object]").
  const toId = (v) => (v && typeof v === "object" ? v._id || v.id || v.value : v);
  const facilityForPayload =
    locType === "facility" ? locId : toId(selClient?.facilityId?.[0]) || null;

  // Shift mode: payload for dragging an associated staff member.
  const payloadFor = (s) => ({
    staffId: s.value,
    type: locType,
    kind: "shift",
    apptTitle: "",
    clientId: locType === "client" ? locId : null,
    facilityId: facilityForPayload,
    startMin: shiftStart,
    endMin: shiftEnd,
  });

  // Appointment mode: add the typed title. If both date and time are set, place
  // it immediately on the calendar; otherwise queue it as a draggable chip.
  const canAddAppt = !!(apptTitle.trim() && locSelected);
  const addAppt = () => {
    if (!canAddAppt) return;
    const title = apptTitle.trim();
    const payload = {
      staffId: null,
      type: locType,
      kind: "appointment",
      apptTitle: title,
      clientId: locType === "client" ? locId : null,
      facilityId: facilityForPayload,
      startMin: apptAllDay ? 8 * 60 : apptStart,
      endMin: apptAllDay ? 18 * 60 : apptEnd,
      allDay: apptAllDay,
    };
    if (apptDate && (apptTime || apptAllDay)) {
      // date + (time or all-day) -> place it directly
      onPlaceNow?.(payload, apptDate);
    } else {
      setPendingAppts((list) => [...list, { id: `${title}-${list.length}-${title.length}-${apptStart}`, title, payload }]);
    }
    setApptTitle("");
  };
  const removeAppt = (id) => setPendingAppts((list) => list.filter((a) => a.id !== id));

  // While docked, the FAB lives only in the toggle pill next to "Add".
  if (!floating) return null;

  // Floating wrapper: default bottom-right corner, or the dragged position.
  const wrapStyle =
    dock.x != null && dock.y != null
      ? { left: dock.x, top: dock.y, right: "auto", bottom: "auto" }
      : { right: 28, bottom: 28 };

  return (
    <div className={css.qbWrap} data-qb-wrap style={wrapStyle}>
      {/* Floating action button (drag to move, click to open) */}
      <button
        type="button"
        className={`${css.qbFab} ${open ? css.qbFabOpen : ""}`}
        aria-label={open ? "Close quick build" : "Quick build a schedule"}
        title={open ? "Close" : "Drag to move · click to quick build"}
        onMouseDown={startFabDrag}
        onClick={() => {
          if (dragRef.current.moved) { dragRef.current.moved = false; return; }
          return open ? closeAll() : setOpen(true);
        }}
      >
        <Icon name={open ? "x" : "plus"} size={24} />
      </button>

      {/* Speed-dial: choose Shift or Appointment */}
      {open && !mode ? (
        <div className={css.qbSpeed}>
          <button type="button" className={css.qbMini} aria-label="Add shift" onClick={() => setMode("shift")}>
            <Icon name="clock" size={19} />
            <span className={css.qbMiniLabel}>Shift</span>
          </button>
          <button type="button" className={css.qbMini} aria-label="Add appointment" onClick={() => setMode("appointment")}>
            <Icon name="calendar" size={19} />
            <span className={css.qbMiniLabel}>Appointment</span>
          </button>
        </div>
      ) : null}

      {open && mode ? (
        <div className={`${css.qbPanel} sd-create qb-inline`} role="dialog" aria-label="Quick build">
          <div className={css.qbHead}>
            <button type="button" className={css.qbBack} aria-label="Back" onClick={() => setMode(null)}>
              <Icon name="chevronLeft" size={16} />
            </button>
            <div className={css.qbTitle}>{isAppt ? "New appointment" : "New shift"}</div>
            <div className={css.qbSub}>
              {isAppt
                ? "Add a titled appointment, then drag it onto a day (or set date and time to place it)."
                : "Pick who it's for, then drag a staff member onto a day."}
            </div>
          </div>

          <div className={css.qbBody}>
            <div className={css.qbField}>
              <span className={css.qbStepLabel}><span className={css.qbNum}>1</span> {isAppt ? "Appointment for" : "Schedule for"}</span>
              <div className={css.qbSeg}>
                <button type="button" className={locType === "client" ? css.qbSegOn : ""} onClick={() => pickType("client")}>
                  <Icon name="person" size={14} /> {isAppt ? "Client" : "Client Visit"}
                </button>
                <button type="button" className={locType === "facility" ? css.qbSegOn : ""} onClick={() => pickType("facility")}>
                  <Icon name="building" size={14} /> Facility
                </button>
              </div>
              <SelectDD
                placeholder={locType === "client" ? "Select a client" : "Select a facility"}
                value={locId}
                options={locOptions}
                onChange={setLocId}
              />
            </div>

            {isAppt ? (
              <>
                <div className={css.qbField}>
                  <span className={css.qbStepLabel}><span className={css.qbNum}>2</span> Title <span className={css.req}>*</span></span>
                  <input
                    className="inp"
                    value={apptTitle}
                    placeholder="e.g. Care plan review"
                    onChange={(e) => setApptTitle(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addAppt(); }}
                  />
                </div>
                <div className={css.qbField}>
                  <span className={css.qbStepLabel}>Date <span className={css.qbOptional}>(optional)</span></span>
                  <div className={css.qbDateWrap} ref={dateRef}>
                    <button
                      type="button"
                      className={`inp ${css.qbDateBtn}`}
                      onClick={() => setDateOpen((o) => !o)}
                    >
                      <span className={apptDate ? "" : "cdd-ph"}>
                        {apptDate ? prettyDate(apptDate) : "Pick a date"}
                      </span>
                      <Icon name="calendar" size={15} />
                    </button>
                    {dateOpen ? (
                      <div className={css.qbDateCal}>
                        <WeekPicker
                          mode="day"
                          monthDate={dateMonth}
                          selDay={apptDate ? parseISODate(apptDate) : null}
                          onMonthChange={setDateMonth}
                          onPick={(d) => {
                            setApptDate(isoDate(d));
                            setDateMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                            setDateOpen(false);
                          }}
                          onClose={() => setDateOpen(false)}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
                <label className="allday-toggle" style={{ marginTop: 2 }}>
                  <input type="checkbox" checked={apptAllDay} onChange={(e) => setApptAllDay(e.target.checked)} />
                  <span className="ad-box"><Icon name="check" size={13} /></span>
                  <span className="ad-txt">All day</span>
                </label>
                {!apptAllDay && (
                  <div className={css.qbField}>
                    <span className={css.qbStepLabel}>Time <span className={css.qbOptional}>(optional)</span></span>
                    <div className={css.qbTimePair}>
                      <input
                        className="inp"
                        type="time"
                        aria-label="Start time"
                        value={apptTime}
                        onChange={(e) => setApptTime(e.target.value)}
                      />
                      <span className={css.qbTimeDash}>–</span>
                      <input
                        className="inp"
                        type="time"
                        aria-label="End time"
                        value={apptEndTime}
                        onChange={(e) => setApptEndTime(e.target.value)}
                      />
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  className={`${css.qbAddBtn}`}
                  disabled={!canAddAppt}
                  onClick={addAppt}
                >
                  <Icon name="plus" size={15} />
                  {apptDate && (apptTime || apptAllDay) ? "Place appointment" : "Add to list"}
                </button>
              </>
            ) : (
              <div className={css.qbField}>
                <span className={css.qbStepLabel}>
                  <span className={css.qbNum}>2</span> Shift <span className={css.qbOptional}>(optional)</span>
                </span>
                <SelectDD
                  placeholder="Default 9:00 AM – 5:00 PM"
                  value={templateKey}
                  options={templateOptions}
                  onChange={setTemplateKey}
                />
                {isCustom && (
                  <div className={css.qbCustomTime}>
                    <label>
                      <span>Start</span>
                      <input type="time" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                    </label>
                    <label>
                      <span>End</span>
                      <input type="time" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Draggable items: staff (shift) or appointment titles (appointment) */}
          <div className={css.qbDragWrap}>
            {isAppt ? (
              pendingAppts.length === 0 ? (
                <div className={css.qbHintMuted}>
                  Added appointments appear here to drag onto a day.
                </div>
              ) : (
                <>
                  <div className={css.qbStaffHead}>Drag an appointment onto a day</div>
                  <div className={css.qbStaffList}>
                    {pendingAppts.map((a) => (
                      <div
                        key={a.id}
                        className={`${css.qbChip} ${dragId === a.id ? css.qbChipDragging : ""}`}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData("application/json", JSON.stringify(a.payload));
                          e.dataTransfer.effectAllowed = "copy";
                          setDragId(a.id);
                        }}
                        onDragEnd={() => setDragId(null)}
                        title="Drag onto a day in the calendar"
                      >
                        <span className={css.qbChipAv}><Icon name="calendar" size={13} /></span>
                        <span className={css.qbChipName}>{a.title}</span>
                        <button
                          type="button"
                          className={css.qbChipRemove}
                          aria-label="Remove"
                          onClick={(e) => { e.stopPropagation(); removeAppt(a.id); }}
                        >
                          <Icon name="x" size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                  {placedCount > 0 ? (
                    <div className={css.qbHint}>Placed. Drag onto more days.</div>
                  ) : null}
                </>
              )
            ) : !locSelected ? (
              <div className={css.qbHintMuted}>
                Select a {locType === "client" ? "client" : "facility"} to see its staff.
              </div>
            ) : dragStaff.length === 0 ? (
              <div className={css.qbHintMuted}>
                No staff linked to this {locType === "client" ? "client" : "facility"}.
              </div>
            ) : (
              <>
                <div className={css.qbStaffHead}>
                  Drag a staff member onto a day{tmpl || isCustom ? ` · ${fmtRange(shiftStart, shiftEnd)}` : ""}
                </div>
                <div className={css.qbStaffList}>
                  {dragStaff.map((s) => (
                    <div
                      key={s.value}
                      className={`${css.qbChip} ${dragId === s.value ? css.qbChipDragging : ""}`}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("application/json", JSON.stringify(payloadFor(s)));
                        e.dataTransfer.effectAllowed = "copy";
                        setDragId(s.value);
                      }}
                      onDragEnd={() => setDragId(null)}
                      title="Drag onto a day in the calendar"
                    >
                      <span className={css.qbChipAv}>
                        {s.photo ? <img src={s.photo} alt="" /> : <Icon name="user" size={13} />}
                      </span>
                      <span className={css.qbChipName}>{s.label}</span>
                      {s.isAdmin ? <span className={css.qbChipPill}>Admin</span> : null}
                      <Icon name="move" size={14} />
                    </div>
                  ))}
                </div>
                {placedCount > 0 ? (
                  <div className={css.qbHint}>Placed. Drag onto more days.</div>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
